import _ from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { EventCreationModalV2 } from "../EventModalV2";
import {
  isSchoolCalendarEvent,
  isProjectDeadlineEvent,
  isProjectDeadlineIAOrEA,
  getFilteredYearGroupsMemoize,
  getFilteredSubjectListMemoize,
  getGradeSelectLabelValue,
  isStaffAdminForProjectModules,
  START_DATE,
  END_DATE,
  getVariablesForCreateEventModalPermissions,
  getTransformedEventDataFromQuery,
  isNewEventCreationFlowEnabled,
  getSelectedAcademicYearForSchool,
  getSelectedSchoolDetailsForCalendar,
} from "MyCalendar/modules/Utils";
import { formatDate } from "Utils";

import {
  createSchoolCalendarEvent,
  editSchoolCalendarEvent,
  updateState,
  updateEventModalV2,
  resetEventModalV2,
} from "MyCalendar/modules/MyCalendarModule";
import {
  updateDeadlineItem,
  createDeadlineItem,
} from "Projects/modules/ProjectModules";

import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import {
  RadioButton,
  Button,
  SelectDropdown,
  ChecklistDropdown,
  FilterLabelButton,
  DatePicker,
  TimePicker,
  Dialog,
  Modal,
  TextInput,
  TextAreaInput,
} from "@toddle-edu/ds-web";

import classes from "./EventCreationModal.scss";

import moment from "moment";

import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import {
  CREATE,
  EDIT,
  DUPLICATE,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import {
  getUserId,
  getOrganizationId,
  getNextOrLastAcademicYearEndDate,
  getUserEntityType,
  getSelectedAcademicYearStartDate,
  getSelectedAcademicYearEndDate,
} from "modules/CommonSelectors";

import {
  getTagList,
  getPersonalProjectGroupId,
  getCommunityProjectGroupId,
  getProjectGroups,
  getGradesWithCurriculam,
  getCurrentCurriculumProgramType,
} from "MyCalendar/modules/Selector";
import {
  getDateMomentObjectCustom,
  formatDateAsUtc,
  isValidDateTimeString,
  checkIsDateAfter,
} from "Utils/DateTimeHelpers";
import { colors } from "Constants";
import {
  getIsCpCoreEnabled,
  getProjectGroupCategoryIdByType,
} from "Projects/modules/Utils";
import classNames from "classnames";
import withControlParameters from "lib/aclService/hoc/withControlParameters";
import { CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION } from "Constants/permissionConstants";
import { WithConfigurationsAndPreferences } from "Utils/ConfigurationUtils/hoc";
import { generateConfigurationQueryVariablesV2 } from "Utils/ConfigurationUtils/Utils";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import ACLStorage from "lib/aclService";

const getProjectGroupId = ({
  yearGroup,
  personalProjectGroupId,
  communityProjectGroupId,
  tag,
  subjectComponent,
}) => {
  if (!yearGroup) {
    if (tag?.value === MY_CALENDAR_CONSTANTS.PERSONAL) {
      return personalProjectGroupId;
    }
    return communityProjectGroupId;
  }
  if (tag?.value === MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT) {
    return subjectComponent?.value;
  }
  return yearGroup?.value;
};

const getDeadlineTypeOptions = ({ t }) => {
  return [
    {
      label: t("calendar:all_day_event"),
      value: MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT,
    },
    {
      label: t("calendar:time_based_event"),
      value: MY_CALENDAR_CONSTANTS.TIME_BASED_EVENT,
    },
  ];
};

const getSubjectList = ({
  yearGroup,
  isEventTypeProjectDeadline,
  isDeadlineIAOrEA,
  projectGroups,
}) => {
  let subjectList = [];
  if (yearGroup && isEventTypeProjectDeadline && isDeadlineIAOrEA) {
    const filteredSubjectList = getFilteredSubjectListMemoize({
      projectGroups,
      yearGroupId: yearGroup?.id,
    });
    subjectList = _.map(filteredSubjectList, (subject, subjectId) => {
      return {
        label: subject?.subjectName,
        value: subjectId,
        subjectComponentList: subject?.subjectComponentList,
      };
    });
  }
  return subjectList;
};

const getSubjectListMemoize = _.memoize(
  params => getSubjectList(params),
  params => JSON.stringify(params)
);

const getSubjectComponentList = ({ subject, subjectList }) => {
  let subjectComponentList = [];
  if (subject) {
    subjectComponentList = _.get(
      _.find(subjectList, { value: subject?.value }),
      "subjectComponentList",
      []
    );
  }
  return subjectComponentList;
};

const getSubjectComponentListMemoize = _.memoize(
  params => getSubjectComponentList(params),
  params => JSON.stringify(params)
);

const initialEventData = {
  eventName: "",
  eventDescription: "",
  eventLocation: "",
  grades: [],
  tag: null,
  yearGroup: null,
  subject: null,
  subjectComponent: null,
};

const getInitialEventData = ({
  eventDetails = {},
  eventType = MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT,
  action = CREATE,
  selectedTimeSlot = {},
}) => {
  const {
    id = null,
    eventName = "",
    eventDescription = "",
    eventLocation = "",
    grades = [],
    tag = null,
    yearGroup = null,
    subject = null,
    subjectComponent = null,
    isDeadlineAllDay,
    isFullDayEvent = false,
    attachments = [],
    sharedWith = "",
    associatedEntities = [],
    eventReminders = [],
    users = [],
    repeatEventSettings = null,
    metadata = {},
  } = eventDetails;

  const gradeIds = _.map(grades, grade => grade?.value);

  const defaultDate = formatDate({ format: DATE_TIME_CONSTANTS.YYYY_MM_DD });
  const defaultTime = formatDate({ format: DATE_TIME_CONSTANTS.HH_mm });

  const {
    startTime = defaultTime,
    endTime = defaultTime,
    startDate = defaultDate,
    endDate = defaultDate,
  } = selectedTimeSlot;

  const deadlineTime = formatDateAsUtc({
    date: startDate,
    format: DATE_TIME_CONSTANTS.HH_mm,
  });

  const scheduleStartEndDateTime = {
    scheduleStartDate: startDate,
    scheduleEndDate: endDate,
    scheduleStartTime: startTime,
    scheduleEndTime:
      isDeadlineAllDay || _.isEqual(action, CREATE) ? endTime : deadlineTime,
  };

  switch (eventType) {
    case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
      switch (action) {
        case CREATE:
          return {
            ...initialEventData,
            ...scheduleStartEndDateTime,
          };
        // default when action is "EDIT" or "DUPLICATE"
        default:
          return {
            ...initialEventData,
            eventName,
            eventDescription,
            eventLocation,
            grades: gradeIds,
            tag,
            isFullDayEvent,
            id,
            attachments,
            associatedEntities,
            sharedWith,
            eventReminders,
            ...scheduleStartEndDateTime,
            users,
            repeatEventSettings,
            metadata,
          };
      }
    default:
      switch (action) {
        case CREATE:
          return {
            ...initialEventData,
            ...scheduleStartEndDateTime,
          };
        default:
          return {
            ...initialEventData,
            eventName,
            eventDescription,
            tag,
            yearGroup,
            subject,
            subjectComponent,
            ...scheduleStartEndDateTime,
          };
      }
  }
};

/**
 * @param {*} gradeList  is an array of object  with keys { label, value, type}
 *  @param {*} curriculumPrograms  is an array of object  with keys { id, acronym }
 * @returns the array of object  except first element all object contains keys {label , options}
 * label (title of curriculam type )
 * options are array of object  each obj have keys {label, value, subText}
 * @description getGradeOptions is a function which group the gradeOptions according to the curriculam type and return the array of object having keys {label , options} except first element have key {isDivider, value}
 */

const getGradeOptions = (gradeList, curriculumPrograms) => {
  // used map to maintain the order of curriculumPrograms
  const curriculamProgramsMap = _.reduce(
    curriculumPrograms,
    (result, program) => {
      const { id, acronym } = program;
      result.set(id, { label: acronym, groupId: id, options: [] });
      return result;
    },
    new Map()
  );

  _.forEach(gradeList, grade => {
    const { curriculumProgram } = grade;
    const { id, acronym } = curriculumProgram;
    const groupObj = curriculamProgramsMap.get(id);

    if (_.isArray(_.get(groupObj, "options"))) {
      groupObj.options = [
        ..._.get(groupObj, "options", []),
        {
          label: _.get(grade, "name"),
          value: _.get(grade, "id"),
        },
      ];
    } else {
      // ideally it will never go to else block, but just in case
      curriculamProgramsMap.set(id, {
        label: `All ${acronym} programs`,
        groupId: id,
        options: [
          {
            label: _.get(grade, "name"),
            value: _.get(grade, "id"),
          },
        ],
      });
    }
  });

  return _.filter(
    [...curriculamProgramsMap.values()],
    groupObj =>
      !_.isEmpty(_.get(groupObj, "options")) &&
      ACLStorage.getModuleSettingsValueByContext(
        CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
        [{ entityId: groupObj?.groupId, entityType: "CURRICULUM_PROGRAM" }]
      )
  );
};

const EventCreationModal = props => {
  //props
  const {
    onCloseEventCreationModal,
    onCloseEventDetailsModal,
    tagList,
    gradeList,
    startDate,
    endDate,
    selectedTimeSlot,
    eventType,
    action,
    userId,
    createSchoolCalendarEvent,
    editSchoolCalendarEvent,
    eventDetails,
    projectGroups = [],
    fetchStatusWiseProjects,
    createDeadlineItem,
    personalProjectGroupId,
    communityProjectGroupId,
    currentCurriculumProgramType,
    updateDeadlineItem,
    t,
    isStaffProjectAdmin,
    setToastMsg,
    dpNextAcademicYearEndDate,
    curriculumPrograms,
    testId,
    updateEventModalV2,
    resetEventModalV2,
  } = props;
  const isDeadlineAllDay = _.get(eventDetails, "isDeadlineAllDay", true);
  const initialStateEventData = getInitialEventData({
    eventDetails,
    eventType,
    action,
    selectedTimeSlot,
  });
  const initialGradesIds = _.get(initialStateEventData, "grades", []);
  //state
  const [createEventDetails, setCreateEventDetails] = useState(
    initialStateEventData
  );
  const [deadlineTypeOption, setDeadlineTypeOption] = useState(
    isDeadlineAllDay
      ? MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT
      : MY_CALENDAR_CONSTANTS.TIME_BASED_EVENT
  );
  const [errors, setErrors] = useState({
    eventName: false,
    tag: false,
    grades: false,
    yearGroup: false,
    eventDescription: false,
    subject: false,
    subjectComponent: false,
  });
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [showDiscardDialogue, setShowDiscardDialogue] = useState(false);

  //variables
  const {
    eventName,
    eventDescription,
    eventLocation,
    tag,
    grades,
    yearGroup,
    subject,
    subjectComponent,
    scheduleStartDate,
    scheduleEndDate,
    scheduleEndTime,
  } = createEventDetails;

  useEffect(() => {
    const eventDetailState = getTransformedEventDataFromQuery({
      ...initialStateEventData,
      academicYearEndDate: endDate,
    });
    updateEventModalV2(eventDetailState);
  }, []);

  const minStartDate = moment(startDate);
  const maxStartDate = moment(endDate);
  const deadlineTypeOptions = getDeadlineTypeOptions({ t });

  const isEventTypeSchoolCalendar = isSchoolCalendarEvent({ eventType });
  const isEventTypeProjectDeadline = isProjectDeadlineEvent({
    eventType,
  });
  const isDeadlineIAOrEA = isProjectDeadlineIAOrEA({ value: tag?.value });

  const yearGroupList = getFilteredYearGroupsMemoize({
    projectGroups,
    tag,
    isEventTypeSchoolCalendar,
  });
  const subjectList = getSubjectListMemoize({
    yearGroup,
    isEventTypeProjectDeadline,
    isDeadlineIAOrEA,
    projectGroups,
  });
  const subjectComponentList = getSubjectComponentListMemoize({
    subject,
    subjectList,
  });
  const tagListSize = _.size(tagList);
  const yearGroupListSize = _.size(yearGroupList);
  const subjectListSize = _.size(subjectList);
  const subjectComponentListSize = _.size(subjectComponentList);

  const showGradesContainer =
    isEventTypeSchoolCalendar ||
    (isEventTypeProjectDeadline && !_.isEmpty(projectGroups));

  const shouldDisableTagSelection =
    isEventTypeProjectDeadline &&
    (action === EDIT || _.isEqual(tagListSize, 1));

  const shouldDisableYearGroupSelection =
    isEventTypeProjectDeadline &&
    (action === EDIT || _.isEqual(yearGroupListSize, 1) || !tag);

  const shouldDisableSubjectSelection =
    isEventTypeProjectDeadline &&
    (action == EDIT || _.isEqual(subjectListSize, 1));

  const shouldDisableSubjectComponentSelection =
    isEventTypeProjectDeadline &&
    (action === EDIT || _.isEqual(subjectComponentListSize, 1) || !subject);

  /**
   * This function is used to set default values (tag, yearGroup, subject, subjectComponent) if,
    there is only one option in respective dropdown.
   */
  const initializeInputFields = () => {
    if (isEventTypeProjectDeadline) {
      if (_.isEqual(tagListSize, 1) && !tag) {
        setCreateEventDetails({
          ...createEventDetails,
          tag: _.head(tagList),
        });
        setErrors({ ...errors, tag: false });
      }
      if (_.isEqual(yearGroupListSize, 1) && !yearGroup) {
        setCreateEventDetails({
          ...createEventDetails,
          yearGroup: _.head(yearGroupList),
        });
        setErrors({ ...errors, yearGroup: false });
      }
    }
    if (isDeadlineIAOrEA) {
      if (_.isEqual(subjectListSize, 1) && !subject) {
        setCreateEventDetails({
          ...createEventDetails,
          subject: _.head(subjectList),
        });
        setErrors({ ...errors, subject: false });
      }
      if (_.isEqual(subjectComponentListSize, 1) && !subjectComponent) {
        setCreateEventDetails({
          ...createEventDetails,
          subjectComponent: _.head(subjectComponentList),
        });
        setErrors({ ...errors, subjectComponent: false });
      }
    }
  };

  useEffect(() => {
    if (action !== EDIT) {
      initializeInputFields();
    }
  }, [tag, yearGroup, subject]);

  //functions
  const getHeaderText = () => {
    const { curriculumAcronym } = props;
    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
        switch (action) {
          case DUPLICATE:
            return `${t("common:duplicate")} ${t("announcement:event")}`;
          case EDIT:
            return `${t("common:update")} ${t("announcement:event")}`;
          default:
            return t("calendar:create_new_event");
        }

      case MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT:
        if (currentCurriculumProgramType === CURRICULUM_TYPE_MYP) {
          switch (action) {
            case DUPLICATE:
              return t("project:duplicate_myp_project_deadline");
            case EDIT:
              return t("project:update_myp_project_deadline");
            default:
              return t("project:create_myp_project_deadline");
          }
        } else if (isStaffProjectAdmin) {
          switch (action) {
            case DUPLICATE:
              return t("project:duplicate_dp_deadline");
            case EDIT:
              return t("project:update_dp_deadline");
            default:
              return getIsCpCoreEnabled()
                ? t("project:create_label_deadline", {
                    label: curriculumAcronym,
                  })
                : t("project:create_dp_deadline");
          }
        } else {
          switch (action) {
            case DUPLICATE:
              return t("project:duplicate_ia_ea_deadline");
            case EDIT:
              return t("project:update_ia_ea_deadline");
            default:
              return t("project:create_ia_ea_deadline");
          }
        }
    }
  };

  const getEventButtonText = () => {
    switch (action) {
      case DUPLICATE:
        return t("calendar:duplicate_event");
      case EDIT:
        return t("calendar:update_event");
      default:
        return t("calendar:create_event");
    }
  };

  const getDeadlineButtonText = () => {
    switch (action) {
      case DUPLICATE:
        return t("common:duplicate_deadline");
      case EDIT:
        return t("common:update_deadline");
      default:
        return t("common:create_deadline");
    }
  };

  const getInputLabels = () => {
    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
        return {
          name: t("calendar:event_name"),
          description: t("common:event_description"),
          descriptionPlaceholder: t(
            "calendar:enter_note_description_and_instructions"
          ),
          tag: t("calendar:select_the_event_tag"),
          placeholder: t("common:enter_title_event"),
          buttonText: getEventButtonText(),
        };

      case MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT:
        return {
          name: t("calendar:deadline_name"),
          description: t("calendar:deadline_description"),
          descriptionPlaceholder: t(
            "calendar:add_the_description_for_the_deadline"
          ),
          tag: t("calendar:select_the_project_tag", {
            acronym: getTagPlaceholderAcronym(),
          }),
          placeholder: t("common:enter_title_deadline"),
          buttonText: getDeadlineButtonText(),
        };
    }
  };

  const getRequiredLabel = label => {
    return (
      <div className={classes.labelContainer}>
        {label}
        <div className={classes.requiredStar}>*</div>
      </div>
    );
  };

  const getTagLabel = () => {
    const { curriculumAcronym } = props;
    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
        return getRequiredLabel(t("calendar:select_the_event_tag"));
      default:
        if (currentCurriculumProgramType === CURRICULUM_TYPE_MYP) {
          return getRequiredLabel(t("common:select_myp_tag"));
        }
        return getIsCpCoreEnabled()
          ? getRequiredLabel(
              `${curriculumAcronym} ${t("calendar:project_tag_all_lowercase")}`
            )
          : getRequiredLabel(
              `${t("common:DP")} ${t("calendar:project_tag_all_lowercase")}`
            );
    }
  };

  const getTagPlaceholderAcronym = () => {
    const { curriculumAcronym } = props;
    if (currentCurriculumProgramType === CURRICULUM_TYPE_MYP) {
      return t("common:MYP");
    }
    return getIsCpCoreEnabled() ? curriculumAcronym : t("common:DP");
  };

  const updateInputField = useCallback(param => {
    const errorField = _.first(_.keys(param));
    setCreateEventDetails(prevCreateEventDetails => ({
      ...prevCreateEventDetails,
      ...param,
    }));
    setErrors(prevErrors => ({ ...prevErrors, [errorField]: false }));
  }, []);

  const setSelectedTag = tag => {
    setCreateEventDetails({
      ...createEventDetails,
      tag,
      yearGroup: null,
      subject: null,
      subjectComponent: null,
    });
    setErrors({ ...errors, tag: false });
  };

  const setSelectedGrades = grades => {
    setCreateEventDetails({ ...createEventDetails, grades });
    setErrors({ ...errors, grades: false });
  };

  const setSelectedYearGroup = yearGroup => {
    setCreateEventDetails({
      ...createEventDetails,
      yearGroup,
      subject: null,
      subjectComponent: null,
    });
    setErrors({ ...errors, yearGroup: false });
  };

  const setSelectedSubject = subject => {
    setCreateEventDetails({
      ...createEventDetails,
      subject,
      subjectComponent: null,
    });
    setErrors({ ...errors, subject: false });
  };

  const setSelectedSubjectComponent = subjectComponent => {
    setCreateEventDetails({ ...createEventDetails, subjectComponent });
    setErrors({ ...errors, subjectComponent: false });
  };

  const getDateChangeHandler = type => {
    return date => {
      const formattedDate = formatDate({
        date,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      });

      const isStartDateAfterEndDate =
        _.isEqual(type, START_DATE) &&
        checkIsDateAfter({
          date1: date,
          date2: getDateMomentObjectCustom(
            scheduleEndDate,
            DATE_TIME_CONSTANTS.YYYY_MM_DD
          ),
        });

      return setCreateEventDetails({
        ...createEventDetails,
        [_.isEqual(type, START_DATE)
          ? "scheduleStartDate"
          : "scheduleEndDate"]: formattedDate,
        ...(isStartDateAfterEndDate ? { scheduleEndDate: formattedDate } : {}),
      });
    };
  };

  const setScheduleEndTime = newScheduleEndTime => {
    setCreateEventDetails({
      ...createEventDetails,
      scheduleEndTime: formatDate({
        date: newScheduleEndTime,
        format: DATE_TIME_CONSTANTS.HH_mm,
      }),
    });
  };

  const onChangeDeadlineType = () => {
    if (deadlineTypeOption === MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT) {
      setDeadlineTypeOption(MY_CALENDAR_CONSTANTS.TIME_BASED_EVENT);
    } else {
      setCreateEventDetails({
        ...createEventDetails,
        scheduleStartDate: formatDate({
          date: scheduleStartDate,
          format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
        }),
      });
      setDeadlineTypeOption(MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT);
    }
  };

  const isEmptyRequiredFields = requiredFields => {
    const errorFields = _.reduce(
      requiredFields,
      (errorFields, field, key) => {
        if (_.isEmpty(_.trim(createEventDetails[key]))) {
          return { ...errorFields, [key]: true };
        }
        //check if the date strings are valid
        if (
          _.includes(["scheduleStartDate", "scheduleEndDate"], key) &&
          !isValidDateTimeString(
            createEventDetails[key],
            DATE_TIME_CONSTANTS.YYYY_MM_DD
          )
        ) {
          return {
            ...errorFields,
            [key]: true,
          };
        }
        return errorFields;
      },
      {}
    );

    if (!_.isEmpty(errorFields)) {
      setErrors({ ...errors, ...errorFields });
      return true;
    }

    return false;
  };

  const validateInputField = () => {
    let inputFieldsToValidate = {};

    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
        inputFieldsToValidate = {
          eventName,
          tag,
          grades,
          scheduleStartDate,
          scheduleEndDate,
        };
        break;
      default:
        if (isDeadlineIAOrEA) {
          inputFieldsToValidate = {
            tag,
            eventName,
            yearGroup,
            subject,
            subjectComponent,
          };
        } else {
          if (_.isEmpty(projectGroups)) {
            inputFieldsToValidate = {
              tag,
              eventName,
            };
          } else {
            inputFieldsToValidate = {
              tag,
              eventName,
              yearGroup,
            };
          }
        }
    }

    if (!isEmptyRequiredFields(inputFieldsToValidate)) {
      return true;
    }

    return false;
  };

  const saveEvent = async inputEventDetails => {
    if (!validateInputField()) {
      setToastMsg("toastMsgs:rectify_the_error_before_proceeding");
      return;
    }
    setShowFullScreenLoader(true);
    try {
      switch (eventType) {
        case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
          await createSchoolCalendarEvent(inputEventDetails);
          break;
        default:
          await createDeadlineItem(inputEventDetails);
      }
    } catch (err) {
      console.error(err);
    } finally {
      onCloseEventDetailsModal
        ? onCloseEventDetailsModal()
        : onCloseEventCreationModal();
    }
    setShowFullScreenLoader(false);
  };

  const editEvent = async inputEventDetails => {
    if (!validateInputField()) {
      setToastMsg("toastMsgs:rectify_the_error_before_proceeding");
      return;
    }
    setShowFullScreenLoader(true);
    try {
      switch (eventType) {
        case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
          await editSchoolCalendarEvent(inputEventDetails);
          break;
        default:
          await updateDeadlineItem(inputEventDetails);
          setToastMsg({
            msg: "toastMsgs:event_update_success",
            type: "success",
          });
      }
    } catch (err) {
      console.error(err);
      setToastMsg("toastMsgs:something_went_wrong");
    } finally {
      onCloseEventDetailsModal
        ? onCloseEventDetailsModal()
        : onCloseEventCreationModal();
    }
    setShowFullScreenLoader(false);
  };

  const scheduleStartDateMomentObject = getDateMomentObjectCustom(
    scheduleStartDate,
    DATE_TIME_CONSTANTS.YYYY_MM_DD
  );

  const scheduleEndDateMomentObject = getDateMomentObjectCustom(
    scheduleEndDate,
    DATE_TIME_CONSTANTS.YYYY_MM_DD
  );

  const scheduleEndTimeMomentObject = getDateMomentObjectCustom(
    scheduleEndTime,
    DATE_TIME_CONSTANTS.HH_mm
  );

  const handleSubmitEvent = () => {
    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT: {
        const inputEventDetails = {
          categoryId: _.get(tag, "value"),
          title: eventName,
          description: eventDescription,
          location: eventLocation,
          start: scheduleStartDate,
          end: scheduleEndDate,
          author: userId,
          action,
        };

        switch (action) {
          case EDIT: {
            const id = _.get(eventDetails, "id", "");
            editEvent({
              ...inputEventDetails,
              entityIds: grades,
              id,
              initialGradesIds,
            });
            break;
          }
          default: {
            saveEvent({ ...inputEventDetails, entityIds: grades });
          }
        }
        break;
      }

      default: {
        const isDeadlineAllDay = _.isEqual(
          deadlineTypeOption,
          MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT
        );

        let conditionalDeadlineDate = scheduleStartDate;
        const deadlineDateMomentObject = scheduleStartDateMomentObject;

        if (!isDeadlineAllDay) {
          deadlineDateMomentObject.hour(scheduleEndTimeMomentObject.hour());
          deadlineDateMomentObject.minute(scheduleEndTimeMomentObject.minute());

          conditionalDeadlineDate = deadlineDateMomentObject;
        } else {
          conditionalDeadlineDate = formatDate({
            date: deadlineDateMomentObject,
            format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
          });
        }

        const inputEventDetails = {
          deadlineItem: {
            title: eventName,
            description: eventDescription,
            deadlineDate: conditionalDeadlineDate,
            isDeadlineAllDay,
          },
          fetchStatusWiseProjects,
        };
        switch (action) {
          case EDIT: {
            const id = _.get(eventDetails, "id", "");
            editEvent({
              ...inputEventDetails,
              deadlineItem: { ...inputEventDetails.deadlineItem, id },
            });
            break;
          }
          default:
            saveEvent({
              ...inputEventDetails,
              projectGroupId: getProjectGroupId({
                yearGroup,
                personalProjectGroupId,
                communityProjectGroupId,
                tag,
                subjectComponent,
              }),
            });
        }
      }
    }
  };

  const onClickModal = e => {
    e?.stopPropagation();
  };

  const renderDeadlineTypeOptions = () => {
    const isTimedDeadlinesEnabled = ACLStore.can(
      "FeatureFlag:EnableTimeBasedProjectDeadlines"
    );

    if (!isTimedDeadlinesEnabled) return null;

    switch (eventType) {
      case MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT:
        return (
          <div className={classes.deadlineTypeContainer}>
            <div className={classNames("text-label", classes.label)}>
              {getRequiredLabel(t("calendar:deadline_type"))}
            </div>
            <div className={classes.deadlineType}>
              {_.map(deadlineTypeOptions, ({ value, label }) => {
                return (
                  <div className={classes.radioButtonItem}>
                    <RadioButton
                      size={"medium"}
                      checked={value === deadlineTypeOption}
                      onChange={onChangeDeadlineType}
                      testId={`${testId}-${label}`}
                      dsVersion={"2.0"}
                    >
                      <div className={classes.radioButtonLabel}>{label}</div>
                    </RadioButton>
                  </div>
                );
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderDateTimeContainer = () => {
    const dpNextAcademicYearEndDateMomentObject = getDateMomentObjectCustom(
      dpNextAcademicYearEndDate,
      DATE_TIME_CONSTANTS.YYYY_MM_DD
    );

    const isDpCurriculum = _.isEqual(
      currentCurriculumProgramType,
      CURRICULUM_TYPE_DP
    );

    const maxDateForDatePicker = isDpCurriculum
      ? dpNextAcademicYearEndDateMomentObject
      : maxStartDate;

    switch (deadlineTypeOption) {
      case MY_CALENDAR_CONSTANTS.ALL_DAY_EVENT:
        switch (eventType) {
          case MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT:
            return (
              <div className={classes.dateTimeContainer}>
                <DatePicker
                  size={"medium"}
                  label={getRequiredLabel(t("classRoom:due_date"))}
                  value={scheduleStartDateMomentObject}
                  onChange={getDateChangeHandler(START_DATE)}
                  minDate={minStartDate}
                  maxDate={maxDateForDatePicker}
                  format={DATE_TIME_CONSTANTS.DD_MMM_YYYY}
                  testId={`${testId}-dueDate`}
                  dsVersion={"2.0"}
                />
              </div>
            );
          default:
            return (
              <div className={classes.datepickerParentContainer}>
                <div
                  className={classNames(
                    "text-label",
                    classes.datepickerTitleContainer
                  )}
                >
                  {t("calendar:event_start_and_end_date")}
                </div>
                <div className={classes.dateTimeContainer}>
                  <div className={classes.dateSelectorField}>
                    <DatePicker
                      size={"medium"}
                      label={getRequiredLabel(t("calendar:start_date"))}
                      value={scheduleStartDateMomentObject}
                      onChange={getDateChangeHandler(START_DATE)}
                      minDate={minStartDate}
                      maxDate={maxDateForDatePicker}
                      format={DATE_TIME_CONSTANTS.DD_MMM_YYYY}
                      testId={`${testId}-startDate`}
                      hasError={errors.scheduleStartDate}
                      dsVersion={"2.0"}
                    />
                  </div>
                  <div className={classes.dateSelectorField}>
                    <DatePicker
                      size={"medium"}
                      label={getRequiredLabel(t("calendar:end_date"))}
                      value={scheduleEndDateMomentObject}
                      onChange={getDateChangeHandler(END_DATE)}
                      minDate={scheduleStartDateMomentObject}
                      maxDate={maxDateForDatePicker}
                      format={DATE_TIME_CONSTANTS.DD_MMM_YYYY}
                      testId={`${testId}-endDate`}
                      hasError={errors.scheduleEndDate}
                      dsVersion={"2.0"}
                    />
                  </div>
                </div>
              </div>
            );
        }
      default:
        return (
          <div className={classes.dateTimeContainer}>
            <div className={classes.dateContainer}>
              <DatePicker
                size={"medium"}
                label={getRequiredLabel(t("classRoom:due_date"))}
                value={scheduleStartDateMomentObject}
                onChange={getDateChangeHandler(START_DATE)}
                minDate={minStartDate}
                maxDate={maxDateForDatePicker}
                format={DATE_TIME_CONSTANTS.DD_MMM_YYYY}
                testId={`${testId}-dueDate`}
                dsVersion={"2.0"}
              />
            </div>
            <div className={classes.timeContainer}>
              <TimePicker
                size={"medium"}
                label={getRequiredLabel(t("common:due_time"))}
                value={scheduleEndTimeMomentObject}
                onChange={setScheduleEndTime}
                testId={`${testId}-dueTime`}
                dsVersion={"2.0"}
              />
            </div>
          </div>
        );
    }
  };

  const anyFieldHasError = _.reduce(
    errors,
    (result, error) => result || error,
    false
  );

  const showDiscardDialogueBox = () => {
    setShowDiscardDialogue(true);
  };

  const hideDiscardDialogueBox = () => {
    setShowDiscardDialogue(false);
  };

  const onDialogConfirmClick = () => {
    hideDiscardDialogueBox();
    onCloseEventCreationModal();
    resetEventModalV2();
  };

  const filterLabelStyle = useMemo(
    () => ({
      width: "100%",
      borderColor: errors.grades ? colors.borderCritical : colors.borderDefault,
    }),
    [errors.grades]
  );

  const handleEventNameChange = event =>
    updateInputField({ eventName: event?.target?.value });

  const handleEventLocationChange = event =>
    updateInputField({ eventLocation: event?.target?.value });

  const handleEventDescriptionChange = event =>
    updateInputField({ eventDescription: event?.target?.value });

  const isMySchoolCalendarEvent =
    MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT === eventType;

  const isNewEventCreationFlowFFEnabled = isNewEventCreationFlowEnabled();
  if (isMySchoolCalendarEvent && isNewEventCreationFlowFFEnabled) {
    return (
      <>
        {showFullScreenLoader ? <FullScreenLoader /> : null}
        <EventCreationModalV2
          onCloseEventCreationModal={onCloseEventCreationModal}
          onCloseEventDetailsModal={onCloseEventDetailsModal}
          showModal={true}
          // createEventContainerClass={createEventContainerClass}
          mode={action || "edit"}
          testId={`${testId}-eventCreationModalV2`}
        />
      </>
    );
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={showDiscardDialogueBox}
      width={"480px"}
      height={"fit-content"}
      shouldCloseOnEsc={true}
      onClose={showDiscardDialogueBox}
      dsVersion={"2.0"}
    >
      <Modal.Header
        title={getHeaderText()}
        isClosable={true}
        hasSeparator="true"
      />

      <Modal.Body isScrollable="true">
        <div className={classes.tagContainer}>
          <div className={classNames("text-label", classes.label)}>
            {getTagLabel()}
          </div>
          <SelectDropdown
            name={"tag"}
            placeholder={_.get(getInputLabels(), "tag")}
            options={tagList}
            value={tag}
            onChange={setSelectedTag}
            isMulti={false}
            isClearable={false}
            size={"medium"}
            error={errors.tag}
            isDisabled={shouldDisableTagSelection}
            testId={`${testId}-eventTag`}
            dsVersion={"2.0"}
          />
        </div>
        <div className={classes.eventName}>
          <TextInput
            label={getRequiredLabel(_.get(getInputLabels(), "name"))}
            size={"medium"}
            name={"eventName"}
            placeholder={_.get(getInputLabels(), "placeholder")}
            value={eventName}
            onChange={handleEventNameChange}
            error={errors.eventName}
            testId={`${testId}-eventName`}
            dsVersion={"2.0"}
          />
        </div>
        {isEventTypeSchoolCalendar ? (
          <div className={classes.eventLocation}>
            <TextInput
              label={t("common:event_location")}
              size={"medium"}
              name={"eventLocation"}
              placeholder={t("common:specify_the_venue")}
              value={eventLocation}
              onChange={handleEventLocationChange}
              testId={`${testId}-eventLocation`}
              dsVersion={"2.0"}
            />
          </div>
        ) : null}
        <div className={classes.eventDescription}>
          <TextAreaInput
            label={_.get(getInputLabels(), "description")}
            minRows={2}
            size={"medium"}
            placeholder={_.get(getInputLabels(), "descriptionPlaceholder")}
            name={"eventDescription"}
            value={eventDescription}
            onChange={handleEventDescriptionChange}
            error={errors.eventDescription}
            testId={`${testId}-eventDescription`}
            dsVersion={"2.0"}
          />
        </div>

        <div className={classes.horizontalDivider}></div>
        {renderDeadlineTypeOptions()}
        {renderDateTimeContainer()}
        <div className={classes.horizontalDivider}></div>
        {showGradesContainer ? (
          <div className={classes.gradesContainer}>
            {isEventTypeSchoolCalendar ? (
              <>
                <div className={classNames("text-label", classes.label)}>
                  {getRequiredLabel(t("common:select_grades"))}
                </div>
                <ChecklistDropdown
                  placement={"bottomRight"}
                  value={grades}
                  options={getGradeOptions(gradeList, curriculumPrograms)}
                  onChange={setSelectedGrades}
                  showAllOption={true}
                  isSearchEnabled={true}
                  testId={`${testId}-gradeSelection`}
                  dsVersion={"2.0"}
                >
                  <FilterLabelButton
                    type={"outlined"}
                    label={t("project:selected")}
                    size={"medium"}
                    labelValue={getGradeSelectLabelValue({
                      grades,
                      gradeList,
                      t,
                    })}
                    style={filterLabelStyle}
                    testId={`${testId}-gradeSelection`}
                    dsVersion={"2.0"}
                  />
                </ChecklistDropdown>
              </>
            ) : null}

            {isEventTypeProjectDeadline && !_.isEmpty(projectGroups) ? (
              <>
                <div className={classNames("text-label", classes.label)}>
                  {getRequiredLabel(t("common:year_group"))}
                </div>
                <SelectDropdown
                  name={"yearGroup"}
                  placeholder={t("calendar:select_applicable_year_group")}
                  options={yearGroupList}
                  value={yearGroup}
                  onChange={setSelectedYearGroup}
                  isMulti={false}
                  isClearable={false}
                  size={"medium"}
                  error={errors.yearGroup}
                  isDisabled={shouldDisableYearGroupSelection}
                  testId={`${testId}-yearGroup`}
                  dsVersion={"2.0"}
                />
              </>
            ) : null}
            {isEventTypeProjectDeadline &&
            !_.isEmpty(projectGroups) &&
            isDeadlineIAOrEA &&
            yearGroup ? (
              <>
                <div className={classes.subjectContainer}>
                  <div className={classNames("text-label", classes.label)}>
                    {getRequiredLabel(t("common:subject"))}
                  </div>
                  <SelectDropdown
                    name={"subject"}
                    placeholder={t("common:select")}
                    options={subjectList}
                    value={subject}
                    onChange={setSelectedSubject}
                    isMulti={false}
                    isClearable={false}
                    size={"medium"}
                    error={errors.subject}
                    isDisabled={shouldDisableSubjectSelection}
                    testId={`${testId}-subject`}
                    dsVersion={"2.0"}
                  />
                </div>
                <div className={classNames("text-label", classes.label)}>
                  {getRequiredLabel(t("common:component"))}
                </div>
                <SelectDropdown
                  name={"componentTag"}
                  placeholder={t("common:select")}
                  options={subjectComponentList}
                  value={subjectComponent}
                  onChange={setSelectedSubjectComponent}
                  isMulti={false}
                  isClearable={false}
                  size={"medium"}
                  error={errors.subjectComponent}
                  isDisabled={shouldDisableSubjectComponentSelection}
                  testId={`${testId}-componentTag`}
                  dsVersion={"2.0"}
                />
              </>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer hasSeparator="true">
        <div className={classes.footer}>
          <Button
            size={"medium"}
            variant={"primary"}
            onClick={handleSubmitEvent}
            disabled={anyFieldHasError}
            testId={`${testId}-${_.get(getInputLabels(), "buttonText")}`}
            dsVersion={"2.0"}
            shortcut={{ code: "Enter", shiftKey: true }}
          >
            {_.get(getInputLabels(), "buttonText")}
          </Button>
          <Button
            variant={"neutral"}
            type={"outlined"}
            size={"medium"}
            onClick={showDiscardDialogueBox}
            className={classes.rightSpace}
            testId={`${testId}-discard`}
            dsVersion={"2.0"}
          >
            {t("common:discard")}
          </Button>
        </div>
      </Modal.Footer>
      {showFullScreenLoader ? <FullScreenLoader /> : null}

      <Dialog
        isOpen={showDiscardDialogue}
        onClose={hideDiscardDialogueBox}
        variant="delete-dialog"
        title={t("common:discard_changes")}
        message={t("common:dicard_the_changes_warning_msg")}
        buttonOneText={t("common:agree_cancel")}
        buttonTwoText={t("common:action_agree", {
          action: t("common:discard"),
        })}
        onButtonOneClick={hideDiscardDialogueBox}
        onButtonTwoClick={onDialogConfirmClick}
        testId={`${testId}-discardChanges`}
        dsVersion={"2.0"}
      />
    </Modal>
  );
};

const mapActionCreators = {
  createSchoolCalendarEvent,
  editSchoolCalendarEvent,
  createDeadlineItem,
  updateDeadlineItem,
  updateState,
  setToastMsg,
  updateEventModalV2,
  resetEventModalV2,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const { userCurriculumPrograms } = userInfo;
  const organizationId = getOrganizationId(state);
  const eventType = _.get(ownProps, "eventType");
  const { t } = ownProps;
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);

  const personalProjectGroupId = getPersonalProjectGroupId(state);
  const communityProjectGroupId = getCommunityProjectGroupId(state);
  const curriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms",
    []
  );
  const curriculumAcronym = _.get(
    state,
    "platform.currentCurriculumProgram.acronym"
  );
  const { currentCurriculumProgramId } = getSelectedSchoolDetailsForCalendar(
    state
  );

  const selectedAcademicYear = getSelectedAcademicYearForSchool(state);
  const selectedAcademicYearStartDate = getSelectedAcademicYearStartDate({
    selectedAcademicYear,
  });
  const selectedAcademicYearEndDate = getSelectedAcademicYearEndDate({
    selectedAcademicYear,
  });

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const isStaffProjectAdmin = isStaffAdminForProjectModules({
    projectGroupCategoryIdByType,
    curriculumProgramId: currentCurriculumProgramId,
  });
  const projectGroups = getProjectGroups(state, isStaffProjectAdmin);

  return {
    userId: getUserId(state),
    userEntityType: getUserEntityType(state),
    tagList: getTagList(
      state,
      eventType,
      currentCurriculumProgramType,
      projectGroups,
      personalProjectGroupId,
      communityProjectGroupId,
      t
    ),
    gradeList: getGradesWithCurriculam(organizationId, eventType),
    startDate: selectedAcademicYearStartDate,
    endDate: selectedAcademicYearEndDate,
    dpNextAcademicYearEndDate: getNextOrLastAcademicYearEndDate({
      organizationId,
    }),
    fetchStatusWiseProjects: true,
    personalProjectGroupId,
    communityProjectGroupId,
    currentCurriculumProgramType,
    projectGroups,
    isStaffProjectAdmin,
    curriculumPrograms,
    curriculumAcronym,
    userCurriculumPrograms,
    courseId: _.get(state, "teacher.selected_class.selected_course", ""),
    learningCourseId: getSelectedLearningCourseId(state),
    isLearningCourseEnabled: getIsLearningCoursesEnabled({
      curriculumProgramId: currentCurriculumProgramId,
    }),
  };
};

export default compose(
  I18nHOC({
    resource: ["commmon", "classRoom", "calendar", "announcement", "project"],
  }),
  connect(mapStateToProps, mapActionCreators),
  withControlParameters({
    options: ({ userCurriculumPrograms }) => {
      return {
        variables: getVariablesForCreateEventModalPermissions({
          curriculumPrograms: userCurriculumPrograms,
        }),
      };
    },
  }),
  WithConfigurationsAndPreferences({
    skip: ({ organizationId }) => !organizationId,
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "ORGANIZATION_LEVEL",
        organizationId,
        modules: ["CALENDAR"],
      }),
    }),
    getConfigurationKeys: () => ["ORGANIZATION"],
  }),
  WithConfigurationsAndPreferences({
    skip: ({ userCurriculumPrograms }) => _.isEmpty(userCurriculumPrograms),
    options: ({ userCurriculumPrograms }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "HOME_PAGE",
        curriculumProgramList: userCurriculumPrograms,
        modules: ["CALENDAR"],
      }),
    }),
  }),
  WithConfigurationsAndPreferences({
    skip: ({ courseId }) => !courseId,
    options: ({ courseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "COURSE_LEVEL",
        courseId,
        modules: ["CALENDAR"],
      }),
    }),
    configurations: { skip: () => true },
  })
)(EventCreationModal);
